import { CompanyOwner, Location, RegionalDivision, User } from '../types/schema';
import { tGroupedEquipment } from '../types';
import { hasPermission, Permission } from './permissions';

/** Junk categories historically created with exact shift-report names (no real equipment). */
const MISNAMED_SHIFT_REPORT_CATEGORY_NAMES = new Set(['start of day', 'end of day']);

export function categoryHasListableUnits(
    equipment: Pick<tGroupedEquipment, 'units'> | { units?: Array<unknown | null> | null },
): boolean {
    return Boolean(equipment.units?.some(Boolean));
}

/** Categories named exactly "Start of Day" / "End of Day" — hide from equipment lists. */
export function isLikelyMisnamedShiftReportCategory(name?: string | null): boolean {
    const normalized = name?.trim().toLowerCase();
    return Boolean(normalized && MISNAMED_SHIFT_REPORT_CATEGORY_NAMES.has(normalized));
}

/**
 * Whether a grouped category should appear on /equipment (and home equipment tiles).
 * Always hides known misnamed shift-report categories.
 * Empty categories are hidden by default; pass `includeEmptyCategories` for managers
 * who need to see newly created categories before units are added.
 */
export function shouldIncludeEquipmentCategoryOnList(
    equipment: Pick<tGroupedEquipment, 'name' | 'units'> | { name?: string | null; units?: Array<unknown | null> | null },
    options?: { includeEmptyCategories?: boolean },
): boolean {
    if (isLikelyMisnamedShiftReportCategory(equipment.name)) {
        return false;
    }
    if (options?.includeEmptyCategories) {
        return true;
    }
    return categoryHasListableUnits(equipment);
}

/** Owners, business admins, and MANAGE_EQUIPMENT users can see empty categories they just created. */
export function canViewEmptyEquipmentCategories(
    companyUser: User | null | undefined,
    companyOwner: CompanyOwner | null | undefined,
    businessAdmin?: unknown,
): boolean {
    if (businessAdmin || companyOwner) {
        return true;
    }
    if (!companyUser) {
        return false;
    }
    return hasPermission(companyOwner ?? null, companyUser, Permission.MANAGE_EQUIPMENT, businessAdmin);
}

/** Full company equipment list (no location-assignment filter on /equipment). */
export function shouldShowAllCompanyEquipmentOnList(
    companyUser: User | null | undefined,
    companyOwner: CompanyOwner | null | undefined,
    businessAdmin?: unknown,
): boolean {
    if (businessAdmin) {
        return true;
    }
    if (companyOwner) {
        return true;
    }
    if (!companyUser) {
        return true;
    }
    if (companyUser.globalLocationPermission) {
        return true;
    }
    return hasPermission(companyOwner ?? null, companyUser, Permission.MANAGE_EQUIPMENT, businessAdmin);
}

export type EquipmentAssignmentScope =
    | { mode: 'all' }
    | { mode: 'none' }
    | { mode: 'assigned'; locationsList: Location[] };

/** Mirrors EquipmentContent searchByAssignments + filterEquipmentByRegion scope. */
export function getEquipmentAssignmentScope(
    companyUser: User | null | undefined,
    companyLocations: Location[],
    companyRegions: RegionalDivision[],
    companyOwner?: CompanyOwner | null,
    businessAdmin?: unknown,
): EquipmentAssignmentScope {
    if (shouldShowAllCompanyEquipmentOnList(companyUser, companyOwner ?? null, businessAdmin)) {
        return { mode: 'all' };
    }

    if (!companyUser) {
        return { mode: 'none' };
    }

    const locationIds = Array.isArray(companyUser.locationAssignment) ? companyUser.locationAssignment : [];
    const regionalIds = Array.isArray(companyUser.regionalAssignment) ? companyUser.regionalAssignment : [];

    if (locationIds.length === 0 && regionalIds.length === 0) {
        return { mode: 'none' };
    }

    const assignedRegionIdSet = new Set(
        (regionalIds.length > 0
            ? companyRegions.filter((region) => regionalIds.includes(region.id))
            : companyRegions
        ).map((region) => region.id),
    );

    let filteredLocations: Location[];

    if (locationIds.length > 0) {
        filteredLocations = companyLocations.filter((location) => locationIds.includes(location.id));
    } else {
        filteredLocations = companyLocations.filter(
            (location) => location.regionalDivisionId && assignedRegionIdSet.has(location.regionalDivisionId),
        );
    }

    if (regionalIds.length > 0) {
        filteredLocations = filteredLocations.filter(
            (location) => location.regionalDivisionId && assignedRegionIdSet.has(location.regionalDivisionId),
        );
    }

    if (!filteredLocations.length) {
        return { mode: 'none' };
    }

    return { mode: 'assigned', locationsList: filteredLocations };
}

type EquipmentListUnitRef = {
    locationId?: string | null;
};

/** Region pills on /equipment — only regions that have at least one assigned unit. */
export function getEquipmentListRegionTabs(
    scope: EquipmentAssignmentScope,
    companyRegions: RegionalDivision[],
    companyLocations: Location[],
    units: Array<EquipmentListUnitRef | null | undefined> = [],
): RegionalDivision[] {
    const locationsInScope = getEquipmentListLocations(scope, companyLocations);
    const locationIdsWithUnits = new Set(
        units
            .map((unit) => unit?.locationId)
            .filter((locationId): locationId is string => Boolean(locationId)),
    );
    const regionIdsWithEquipment = new Set(
        locationsInScope
            .filter(
                (location) =>
                    Boolean(location.id) &&
                    locationIdsWithUnits.has(location.id) &&
                    Boolean(location.regionalDivisionId),
            )
            .map((location) => location.regionalDivisionId as string),
    );
    return companyRegions.filter((region) => regionIdsWithEquipment.has(region.id));
}

/** Locations used to match units to a region tab. */
export function getEquipmentListLocations(
    scope: EquipmentAssignmentScope,
    companyLocations: Location[],
): Location[] {
    if (scope.mode === 'all') {
        return companyLocations;
    }
    if (scope.mode === 'none') {
        return [];
    }
    return scope.locationsList;
}

/** Grouped equipment types visible on home / list views for the user's assignment scope. */
export function filterGroupedEquipmentByAssignmentScope(
    companyGroupedEquipmentData: tGroupedEquipment[],
    scope: EquipmentAssignmentScope,
): tGroupedEquipment[] {
    if (scope.mode === 'all') {
        return companyGroupedEquipmentData.filter((equipment) => shouldIncludeEquipmentCategoryOnList(equipment));
    }

    if (scope.mode === 'none') {
        return [];
    }

    const assignedLocationIds = new Set(scope.locationsList.map((location) => location.id));

    return companyGroupedEquipmentData.filter((equipment) => {
        if (!shouldIncludeEquipmentCategoryOnList(equipment)) {
            return false;
        }

        return equipment.units.some((unit) => {
            if (!unit) {
                return false;
            }

            if (!unit.locationId) {
                return true;
            }

            return assignedLocationIds.has(unit.locationId);
        });
    });
}

/** Unit count for the Equipment page "All Equipment" tab. */
export function countAllTabEquipmentUnits(
    companyGroupedEquipmentData: tGroupedEquipment[],
    scope: EquipmentAssignmentScope,
): number {
    if (scope.mode === 'none') {
        return 0;
    }

    if (scope.mode === 'all') {
        return companyGroupedEquipmentData.reduce(
            (total, equipment) => total + (equipment.units?.filter(Boolean).length ?? 0),
            0,
        );
    }

    return companyGroupedEquipmentData.reduce((total, equipment) => {
        const matchingUnits =
            equipment.units?.filter((unit) => {
                if (!unit) {
                    return false;
                }
                if (!unit.locationId) {
                    return true;
                }
                return scope.locationsList.some((location) => location.id === unit.locationId);
            }) ?? [];

        return total + matchingUnits.length;
    }, 0);
}
